import React from 'react';
import { BookOpen, CalendarDays, Dumbbell, Home, Megaphone, MessageSquare, Ticket, User } from 'lucide-react';
import { formatDate, formatDateTime } from './format';

export const SEARCH_GROUPS = [
  { type: 'tickets', label: 'Tickets', icon: Ticket },
  { type: 'members', label: 'Residents', icon: User },
  { type: 'units', label: 'Units', icon: Home },
  { type: 'bulletins', label: 'Bulletins', icon: Megaphone },
  { type: 'calendar', label: 'Calendar', icon: CalendarDays },
  { type: 'forum', label: 'Forum', icon: MessageSquare },
  { type: 'handbook', label: 'Handbook', icon: BookOpen },
  { type: 'amenities', label: 'Amenities', icon: Dumbbell }
];

const query = (params) => new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();

// What a search result shows and where it opens: the tab with the item's side panel.
// Bulletins and events are also filtered by title so the item is on the first page.
export function describeResult(type, item, slug) {
  const base = `/admin/${slug}`;
  switch (type) {
    case 'tickets':
      return {
        title: [item.ticketNumber, item.title].filter(Boolean).join(' '),
        meta: `${item.status} · ${item.priority}`,
        action: item.status === 'closed' ? 'Reopen' : 'Edit',
        to: `${base}/tickets?${query({ status: 'all', ticket: item._id })}`
      };
    case 'members':
      return {
        title: item.name,
        meta: [item.unit != null ? `Unit ${item.unit}` : null, item.role, item.isActive === false ? 'waiting for approval' : null].filter(Boolean).join(' · '),
        action: item.isActive === false ? 'Review' : 'Edit',
        to: `${base}/residents?${query({ user: item._id })}`
      };
    case 'units':
      return { title: `Unit ${item.number}`, meta: item.type || 'Unit', action: 'Edit', to: `${base}/units?${query({ unit: item._id })}` };
    case 'bulletins':
      return { title: item.title, meta: `Posted ${formatDate(item.createdAt)}`, action: 'Edit', to: `${base}/bulletins?${query({ q: item.title, bulletin: item._id })}` };
    case 'calendar': {
      const past = item.endTime ? new Date(item.endTime) < new Date() : false;
      return {
        title: item.title,
        meta: formatDateTime(item.startTime),
        action: 'Edit',
        to: `${base}/calendar?${query({ when: past ? 'past' : null, q: item.title, event: item._id })}`
      };
    }
    case 'forum':
      return { title: item.content, meta: `Forum post by ${item.userName}`, action: 'Moderate', to: `${base}/forum?${query({ post: item._id })}` };
    case 'handbook':
      return { title: item.title, meta: 'Handbook', action: 'Edit', to: `${base}/handbook?${query({ document: item._id })}` };
    case 'amenities':
      return { title: item.name, meta: item.isActive === false ? 'Inactive' : 'Amenity', action: 'Edit', to: `${base}/amenities?${query({ amenity: item._id })}` };
    default:
      return { title: item.title || item.name || '', meta: '', action: 'Open', to: base };
  }
}

export function Highlight({ snippet }) {
  if (!snippet?.text) return null;
  const { text, matchStart, matchEnd } = snippet;
  return (
    <>
      {text.slice(0, matchStart)}
      <mark className="bg-warning/30 text-foreground rounded-sm">{text.slice(matchStart, matchEnd)}</mark>
      {text.slice(matchEnd)}
    </>
  );
}

export function SearchResultRow({ group, item, slug, selected, onSelect, id }) {
  const view = describeResult(group.type, item, slug);
  const Icon = group.icon;
  const snippet = item.snippets?.[0];
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={!!selected}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onSelect(view.to)}
      className={`flex w-full items-center gap-3 px-3 py-2 text-left rounded-lg transition-colors ${selected ? 'bg-secondary' : 'hover:bg-muted/50'}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><Icon className="h-4 w-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-foreground truncate">{view.title}</span>
        <span className="block text-xs text-muted-foreground truncate">
          {view.meta}{snippet && snippet.field !== 'title' && snippet.field !== 'name' ? <> · <Highlight snippet={snippet} /></> : null}
        </span>
      </span>
      <span className="shrink-0 text-xs px-2 py-1 rounded border border-border text-foreground">{view.action}</span>
    </button>
  );
}
